import { useToast } from "../components/ui/Toast";
import { classifyApiError } from "../core/errors/classify.js";
import { repeatCount } from "../core/errors/coalesce.js";
import { reportClientError } from "../core/errorReporter.js";
import { markErrorReported } from "../core/errors/reported.js";

/**
 * The only way an API failure reaches the screen.
 *
 * Classifies the error, shows one toast per problem, and reports EVERY
 * occurrence to Monitoring — the toast is collapsed, the record is not.
 *
 * Usage:
 *   const { showApiError } = useApiError();
 *   catch (err) { showApiError(err, "save the description"); }
 *
 * The second argument is what the user was trying to do, in the same form the
 * classifier builds from the URL ("save the listing"). Pass it when the URL
 * alone would not make the operation obvious.
 */

const TYPE_BY_CODE = {
  SESSION_EXPIRED: "api_session_expired",
  FORBIDDEN: "api_forbidden",
  NOT_FOUND: "api_not_found",
  CONFLICT: "api_conflict",
  VALIDATION: "api_validation",
  TOO_LARGE: "api_payload_too_large",
  RATE_LIMITED: "api_rate_limited",
  PAYMENT_FAILED: "api_payment_error",
  OFFLINE: "api_network_error",
  UNREACHABLE: "api_network_error",
  TIMEOUT: "api_timeout",
};

export function showApiError(
  toast,
  error,
  fallbackPhrase,
  { notify = true, serverMessage = false } = {}
) {
  const result = classifyApiError(error, {
    fallback: fallbackPhrase,
    // Opt-in, for the handful of endpoints whose failures are written for
    // merchants rather than for us — a Xero invoice refused because an account
    // code is unmapped says something we cannot reconstruct from the status.
    // Still sanitiser-gated: a message that looks technical is discarded like
    // any other, so this cannot reintroduce the leak.
    preferServerMessage: serverMessage,
  });

  // Whatever happens next, this failure has been dealt with here: either it is
  // reported below, or the classifier decided it is not worth reporting
  // (cancelled, killed by the browser as the tab hid, plan-gating 403). Either
  // way the window "unhandledrejection" net must not record it again when the
  // caller re-throws it without a catch.
  markErrorReported(error);

  // Silent requests — including the error reporter's own endpoint — never
  // toast and never report. Reporting a failed report loops.
  if (result.silent) return result;

  reportClientError({
    type: TYPE_BY_CODE[result.code] ?? "api_server_error",
    message: result.code,
    context: {
      ref: result.ref,
      code: result.code,
      status: result.status,
      requestUrl: result.requestUrl,
      // The raw backend text. It belongs in the record, never on screen.
      responseMessage: result.rawMessage,
      stack: error?.stack ?? null,
    },
  });

  // `notify: false` records without interrupting: used for background GET
  // failures, which the user did not ask for and cannot act on. The capture
  // still happens — that is the point of separating the two.
  if (!notify) return result;

  const key = `${result.code}|${result.requestUrl}`;
  const repeat = repeatCount(key);

  // Reusing the id makes the Toast provider replace the existing toast rather
  // than stack another one, so a burst stays a single notification.
  toast({
    id: key,
    tone: "error",
    title: result.message,
    description:
      repeat > 1 ? `Ref ${result.ref} · ${repeat} times` : `Ref ${result.ref}`,
  });

  return result;
}

export function useApiError() {
  const toast = useToast();

  return {
    showApiError: (error, fallbackPhrase, options) =>
      showApiError(toast, error, fallbackPhrase, options),
  };
}
